import type { SpellOccurrence } from './types/spell-occurrence'
import type { SpellIssue } from './types/spell-issue'
import type { SpellCheckState } from './types/spell-check-state'

export function openOccurrenceCount(issue: SpellIssue): number {
  return issue.occurrences.filter((o) => o.status === 'open').length
}

/** Issues that still have at least one open instance — what the panel lists. */
export function visibleSpellIssues(state: SpellCheckState): SpellIssue[] {
  return state.issues.filter((issue) => openOccurrenceCount(issue) > 0)
}

/** Total open instances across all issues — the "N Misspellings" badge. */
export function totalMisspellings(state: SpellCheckState): number {
  return state.issues.reduce((sum, issue) => sum + openOccurrenceCount(issue), 0)
}

/** First open instance index, or 0 when none are open. */
export function firstOpenOccurrenceIndex(issue: SpellIssue): number {
  const index = issue.occurrences.findIndex((o) => o.status === 'open')

  return index === -1 ? 0 : index
}

function updateIssue(
  state: SpellCheckState,
  issueId: string,
  update: (issue: SpellIssue) => SpellIssue
): SpellCheckState {
  return {
    ...state,
    issues: state.issues.map((issue) => (issue.id === issueId ? update(issue) : issue)),
  }
}

/**
 * Marks the chosen open instances, keeping where each one is: the choice has to stay on that
 * instance as the text is edited (`trackResolutionsThroughEdits`).
 */
function resolveOccurrences(
  issue: SpellIssue,
  shouldResolve: (index: number) => boolean,
  resolution: SpellOccurrence
): SpellIssue {
  return {
    ...issue,
    occurrences: issue.occurrences.map((occurrence, index) =>
      occurrence.status === 'open' && shouldResolve(index)
        ? { ...occurrence, ...resolution }
        : occurrence
    ),
  }
}

export function changeOccurrence(
  state: SpellCheckState,
  issueId: string,
  occurrenceIndex: number,
  replacement: string
): SpellCheckState {
  return updateIssue(state, issueId, (issue) =>
    resolveOccurrences(issue, (index) => index === occurrenceIndex, {
      status: 'changed',
      replacement,
    })
  )
}

export function changeAllOccurrences(
  state: SpellCheckState,
  issueId: string,
  replacement: string
): SpellCheckState {
  return updateIssue(state, issueId, (issue) =>
    resolveOccurrences(issue, () => true, { status: 'changed', replacement })
  )
}

export function ignoreOccurrence(
  state: SpellCheckState,
  issueId: string,
  occurrenceIndex: number
): SpellCheckState {
  return updateIssue(state, issueId, (issue) =>
    resolveOccurrences(issue, (index) => index === occurrenceIndex, { status: 'ignored' })
  )
}

export function ignoreAllOccurrences(state: SpellCheckState, issueId: string): SpellCheckState {
  return updateIssue(state, issueId, (issue) => ({
    ...resolveOccurrences(issue, () => true, { status: 'ignored' }),
    ignoredAll: true,
  }))
}

/** Whether two words are the same word under the Case Sensitive setting (ED-5). */
export function wordsMatch(a: string, b: string, caseSensitive: boolean): boolean {
  return caseSensitive ? a === b : a.toLowerCase() === b.toLowerCase()
}

/**
 * Adds `word` to the custom dictionary and marks every open instance it now covers `added`.
 * Matching follows `caseSensitive`, mirroring legacy: ON needs an exact-case match against a
 * custom entry.
 */
export function addCustomWord(state: SpellCheckState, rawWord: string): SpellCheckState {
  const word = rawWord.trim()

  // With Case Sensitive OFF, "zylostrab" is already in a dictionary that has "Zylostrab" (ED-5).
  if (!word || state.customWords.some((w) => wordsMatch(w, word, state.caseSensitive))) return state

  return {
    ...state,
    customWords: [...state.customWords, word],
    issues: state.dictionaries.custom
      ? state.issues.map((issue) =>
          wordsMatch(issue.word, word, state.caseSensitive)
            ? resolveOccurrences(issue, () => true, { status: 'added' })
            : issue
        )
      : state.issues,
  }
}

/**
 * Marks every open instance of the issue `added`, even with the Custom Dictionary toggle off, so
 * the word leaves the list either way. It never touches `customWords`: the fix popover saves the
 * word to the backend first and marks it only once the backend has it (SC-16).
 */
export function markIssueAdded(state: SpellCheckState, issueId: string): SpellCheckState {
  return updateIssue(state, issueId, (issue) =>
    resolveOccurrences(issue, () => true, { status: 'added' })
  )
}

/** Adds the issue's word to `customWords` and marks the issue added (`markIssueAdded`). */
export function addIssueToDictionary(state: SpellCheckState, issueId: string): SpellCheckState {
  const issue = state.issues.find((i) => i.id === issueId)

  if (!issue) return state

  return markIssueAdded(addCustomWord(state, issue.word), issueId)
}

/**
 * Reopens the instances Add to Dictionary resolved once their word has left the custom dictionary
 * (removed in Edit Dictionary, cleared, or removed by another user), so it is flagged again
 * (WMPCM-13377). Ignore and Ignore All choices are kept: those never depended on the dictionary.
 */
export function reopenRemovedWords(
  issues: readonly SpellIssue[],
  customWords: readonly string[],
  caseSensitive: boolean
): SpellIssue[] {
  return issues.map((issue) =>
    issue.occurrences.some((o) => o.status === 'added') &&
    !customWords.some((w) => wordsMatch(w, issue.word, caseSensitive))
      ? {
          ...issue,
          occurrences: issue.occurrences.map((o) =>
            o.status === 'added' ? { ...o, status: 'open' as const } : o
          ),
        }
      : issue
  )
}

export function removeCustomWord(state: SpellCheckState, word: string): SpellCheckState {
  return { ...state, customWords: state.customWords.filter((w) => w !== word) }
}

export function clearCustomWords(state: SpellCheckState): SpellCheckState {
  return { ...state, customWords: [] }
}
